import React, { useEffect, useState } from 'react';
import { authClient } from '../../authClient.js';
import { PASSWORD_POLICY_MESSAGE, PASSWORD_RULE_TEXT, meetsPasswordPolicy } from '../../passwordPolicy.js';
// The signup funnel's artwork, not a second copy of it: this card and the
// funnel's sign-in screen are the same design, and a redrawn paw or Google mark
// would drift the moment one of them was touched. icons.tsx is inline SVG with
// no dependencies, so pulling it into the entry chunk costs nothing.
import { PawsMark, GoogleG, AppleLogo, Tick, WarnCircle } from '../welcome/icons.js';
import './auth.css';

type Mode = 'signin' | 'signup' | 'forgot';

export const Login: React.FC = () => {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const returnTo = `${window.location.origin}/portal`;

  /**
   * `allowSignUp` is off by default: this is the sign-in page, and social
   * providers are not allowed to mint accounts (see the server's
   * socialProviders config). An unknown address comes back to
   * `errorCallbackURL` as ?error=signup_disabled, which the effect below turns
   * into a message. The beta invite deep-link is the exception — it arrives as
   * ?sso=google&signup=1, issued only after the invite password was verified.
   */
  async function social(
    provider: 'google' | 'apple',
    dest: string = returnTo,
    allowSignUp = false,
  ) {
    setError(null); setNotice(null); setBusy(true);
    try {
      await authClient.signInSocial(provider, dest, {
        errorCallbackURL: returnTo,
        ...(allowSignUp ? { requestSignUp: true } : {}),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
      setBusy(false);
    }
  }

  // Better Auth bounces a failed OAuth round-trip back here with ?error=… .
  // Strip it from the URL afterwards so a refresh doesn't re-show a stale error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (!err) return;
    setError(
      err === 'signup_disabled'
        ? 'No PawsFree account for that address yet. Create one at pawsfree.net, then sign in here.'
        : 'Sign-in failed. Please try again.',
    );
    params.delete('error');
    params.delete('error_description');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  // Deep-link auto-start: /portal?sso=google|apple[&next=/path]. Used by the
  // legacy /auth/google/start redirect and the beta invite's "Continue with
  // Google", so those entrypoints flow straight into Better Auth.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sso = params.get('sso');
    if (sso !== 'google' && sso !== 'apple') return;
    const next = params.get('next');
    const dest = next && next.startsWith('/') ? `${window.location.origin}${next}` : returnTo;
    void social(sso, dest, params.get('signup') === '1');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setNotice(null); setBusy(true);
    try {
      if (mode === 'signin') {
        await authClient.signInEmail(email, password);
        window.location.assign('/portal');
        return;
      }
      if (mode === 'signup') {
        // Checked here so the rule is a sentence rather than whatever Better
        // Auth's rejection reads like; the app server enforces it either way.
        if (!meetsPasswordPolicy(password)) { setError(PASSWORD_POLICY_MESSAGE); return; }
        await authClient.signUpEmail(email, password, name, returnTo);
        setNotice('Almost there. Check your email to verify your account, then sign in.');
        setMode('signin');
      } else {
        await authClient.requestPasswordReset(email, `${window.location.origin}/reset-password`);
        setNotice('If that email has an account, a reset link is on its way.');
        setMode('signin');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const go = (next: Mode) => { setMode(next); setError(null); setNotice(null); };

  const title = mode === 'signup' ? 'Create your account'
    : mode === 'forgot' ? 'Reset your password'
    : 'Welcome back';
  const sub = mode === 'signup' ? 'We’ll email you a link to verify your address.'
    : mode === 'forgot' ? 'We’ll email you a link to set a new one.'
    : null;

  return (
    <div className="pf-auth">
      <div className="pf-auth-card">
        <div className="pf-auth-hero">
          <div className="pf-auth-mark"><PawsMark size={72} /></div>
          <div className="pf-auth-wordmark">Paws<em>Free</em></div>
          <div className="pf-auth-eyebrow">Account dashboard</div>
        </div>

        <div className="pf-auth-body">
          <h1 className="pf-auth-title">{title}</h1>
          {sub && <p className="pf-auth-sub">{sub}</p>}

          {mode !== 'forgot' && (
            <>
              <div>
                <button type="button" className="pf-auth-oauth" disabled={busy} onClick={() => void social('google')}>
                  <GoogleG /> Continue with Google
                </button>
                <button type="button" className="pf-auth-oauth pf-auth-apple" disabled={busy} onClick={() => void social('apple')}>
                  <AppleLogo /> Continue with Apple
                </button>
              </div>
              <div className="pf-auth-or"><span /><b>or</b><span /></div>
            </>
          )}

          <form className="pf-auth-form" onSubmit={submit}>
            {mode === 'signup' && (
              <label className="pf-auth-field">
                <span>Full name</span>
                <input className="pf-auth-input" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </label>
            )}
            <label className="pf-auth-field">
              <span>Email</span>
              <input className="pf-auth-input" type="email" placeholder="you@practice.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </label>
            {mode !== 'forgot' && (
              <label className="pf-auth-field">
                <span>Password</span>
                <input
                  className="pf-auth-input"
                  type="password"
                  // The rule is only a rule when they're choosing one; on
                  // sign-in it would read as advice about the password they
                  // already have — which may predate the policy and still works.
                  placeholder={mode === 'signup' ? 'At least 12 characters' : 'Your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  aria-describedby={mode === 'signup' ? 'pf-pw-rule' : undefined}
                />
                {/* Not a <span>: .pf-auth-field > span is the bold label style. */}
                {mode === 'signup' && (
                  <small className="pf-auth-hint" id="pf-pw-rule">{PASSWORD_RULE_TEXT}</small>
                )}
              </label>
            )}

            {error && <div className="pf-auth-msg pf-auth-err" role="alert"><WarnCircle />{error}</div>}
            {notice && <div className="pf-auth-msg pf-auth-ok" role="status"><Tick size={16} width={3} />{notice}</div>}

            <button type="submit" className="pf-auth-cta" disabled={busy}>
              {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
            </button>
          </form>

          <div className="pf-auth-foot">
            {mode === 'signin' ? (
              <>
                <button type="button" className="pf-auth-link" onClick={() => go('forgot')}>Forgot password?</button>
                <span>·</span>
                <button type="button" className="pf-auth-link" onClick={() => go('signup')}>Create account</button>
              </>
            ) : (
              <button type="button" className="pf-auth-link" onClick={() => go('signin')}>← Back to sign in</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
